/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { memo, ReactNode } from 'react'
import { Tooltip } from 'antd'
import {
  CheckCircleOutlined,
  CheckSquareOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FileTextOutlined,
  HistoryOutlined,
  MinusCircleOutlined,
  StopOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import Text from 'src/components/ts-components/Text'
import {
  resolveWaiverStatus,
  WaiverIconKind,
  WaiverStatusInput,
  WaiverTone,
} from './resolveWaiverStatus'

export type { WaiverStatus, WaiverEvidenceLevel } from './resolveWaiverStatus'

interface Props extends WaiverStatusInput {
  /** The pill's action, e.g. sign or unsign. It only looks clickable when provided. */
  onClick?: () => void
  /** Replaces the resolved label, e.g. "Waiver signed" where no column header says what the pill is about. */
  label?: string
  /** Replaces the resolved tooltip copy. */
  tooltip?: ReactNode
}

export const TONE_COLORS: Record<
  WaiverTone,
  { accent: string; border: string }
> = {
  success: { accent: colors.SUCCESS_200, border: colors.SUCCESS_200 },
  info: { accent: colors.INFO_200, border: colors.INFO_200 },
  warning: { accent: colors.WARNING_200, border: colors.WARNING_200 },
  error: { accent: colors.ERROR_200, border: colors.ERROR_200 },
  neutral: { accent: colors.NEUTRAL_100, border: colors.NEUTRAL_200 },
}

export const ICONS: Record<WaiverIconKind, typeof CheckCircleOutlined> = {
  signed: CheckCircleOutlined,
  guardian: TeamOutlined,
  paper: FileTextOutlined,
  legacy: HistoryOutlined,
  outdated: ExclamationCircleOutlined,
  not_signed_required: CloseCircleOutlined,
  not_signed: MinusCircleOutlined,
  revoked: StopOutlined,
}

/** The pill reads as a checkbox; the dense table icon keeps the circle. */
const TAG_ICONS = { ...ICONS, signed: CheckSquareOutlined }

const WaiverStatusTag = ({ onClick, label, tooltip, ...input }: Props) => {
  const appearance = resolveWaiverStatus(input)
  if (!appearance) {
    return null
  }

  const { accent, border } = TONE_COLORS[appearance.tone]
  const IconComponent = TAG_ICONS[appearance.icon]

  const pillStyle = css`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 8px;
    border-radius: 8px;
    min-width: 90px;
    white-space: nowrap;
    background-color: ${accent}26;
    border: 1px solid ${border}80;
    cursor: ${onClick ? 'pointer' : 'default'};

    ${onClick &&
    `
      &:hover {
        opacity: 0.85;
      }
    `}

    p {
      white-space: nowrap;
    }
  `

  const content = (
    <>
      <IconComponent
        css={css`
          color: ${accent};
          font-size: 12px;
        `}
      />
      <Text variant='paragraphXSmall' weight='medium' color={colors.NEUTRAL_0}>
        {label ?? appearance.label}
      </Text>
    </>
  )

  return (
    <Tooltip title={tooltip ?? appearance.tooltip}>
      {onClick ? (
        <button type='button' css={pillStyle} onClick={onClick}>
          {content}
        </button>
      ) : (
        <span css={pillStyle}>{content}</span>
      )}
    </Tooltip>
  )
}

export default memo(WaiverStatusTag)
